import { useState } from "react";
import { Box, Text, Flex, Button, Icon, Divider, VStack, HStack, Badge } from "@chakra-ui/react";
import moment from "moment";
import { formatMoney } from "../../services/utils/helpersFunc";
import { BiCalendar, BiCheckCircle, BiErrorCircle, BiRefresh } from "react-icons/bi";
import MarkAsPaidModal from "../Modals/MarkAsPaidModal";
import RetryDebitModal from "../Modals/RetryDebitModal";
import SectionLayout from "./SectionLayout";
import { motion } from "framer-motion";

const MotionFlex = motion(Flex);

const RepaymentItem = ({ schedule, onMarkAsPaid, onRetryDebit }) => {
  const isPaid = schedule?.status === "Paid";
  
  return (
    <MotionFlex
      align="center"
      p="4"
      borderRadius="lg"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ bg: "gray.50" }}
      transition={{ duration: 0.3 }}
      justify="space-between"
      wrap={{ base: "wrap", md: "nowrap" }}
      gap="4"
    >
      <HStack spacing="4" flex="1">
        <Flex
          w="40px"
          h="40px"
          borderRadius="full"
          bg="brand.50"
          align="center"
          justify="center"
        >
          <Icon as={BiCalendar} w={5} h={5} color="brand.500" />
        </Flex>
        
        <Box>
          <Text fontWeight="600" fontSize="14px" color="text.primary">
            Due: {moment(schedule?.date, "YYYY-MM-DD").format("DD MMM YYYY")}
          </Text>
          <Text fontSize="13px" fontWeight="700" color="brand.500">
            ₦{formatMoney(schedule?.amount_due)}
          </Text>
        </Box>
      </HStack>
      
      <HStack spacing="4">
        <Badge
          px="3"
          py="1"
          borderRadius="full"
          colorScheme={isPaid ? "green" : "red"}
          display="flex"
          alignItems="center"
          gap="1"
        >
          <Icon as={isPaid ? BiCheckCircle : BiErrorCircle} />
          {schedule?.status}
        </Badge>
        
        {!isPaid && (
          <HStack spacing="2">
            <Button
              size="sm"
              leftIcon={<BiCheckCircle />}
              variant="outline"
              colorScheme="brand"
              onClick={() => onMarkAsPaid(schedule)}
              fontSize="11px"
            >
              Mark Paid
            </Button>
            <Button
              size="sm"
              leftIcon={<BiRefresh />}
              bg="orange.400"
              color="white"
              _hover={{ bg: "orange.500" }}
              onClick={() => onRetryDebit(schedule)}
              fontSize="11px"
            >
              Retry
            </Button>
          </HStack>
        )}
      </HStack>
    </MotionFlex>
  );
};

const RepaymentSchedule = ({ loanRequest }) => {
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRetryDebitModalOpen, setIsRetryDebitModalOpen] = useState(false);

  const schedules = loanRequest?.repaymentSchedule || [];

  const openMarkAsPaidModal = (schedule) => {
    setSelectedSchedule(schedule);
    setIsModalOpen(true);
  };

  const openRetryDebitModal = (schedule) => {
    setSelectedSchedule(schedule);
    setIsRetryDebitModalOpen(true);
  };

  return (
    <SectionLayout
      title="Repayment Schedules"
      gridProps={{ columns: 1, spacing: 1 }}
    >
      {schedules.length > 0 ? (
        <VStack align="stretch" spacing={2} divider={<Divider />}>
          {schedules.map((schedule, index) => (
            <RepaymentItem 
              key={index} 
              schedule={schedule} 
              onMarkAsPaid={openMarkAsPaidModal}
              onRetryDebit={openRetryDebitModal}
            />
          ))}
        </VStack>
      ) : (
        <Flex justify="center" py="8">
          <Text color="text.muted" fontSize="14px">
            No repayment schedule available
          </Text>
        </Flex>
      )}

      <MarkAsPaidModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        schedule={selectedSchedule}
      />

      <RetryDebitModal
        isOpen={isRetryDebitModalOpen}
        onClose={() => setIsRetryDebitModalOpen(false)}
        schedule={selectedSchedule}
      />
    </SectionLayout>
  );
};

export default RepaymentSchedule;
